import AirportShuttle from '@mui/icons-material/AirportShuttle';
import LocalGroceryStore from '@mui/icons-material/LocalGroceryStore';
import Settings from '@mui/icons-material/Settings';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Skeleton from '@mui/material/Skeleton';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CanvasCell, CellRow, ColorIndicator, CustomInput, CustomOutput } from '@qpr/components';
import React from 'react';
import { CanvasCellState } from '../sipoc-state';

const PREFIX = 'SipocGrid';

const classes = {
  canvasCell: `${PREFIX}-canvasCell`,
  modal: `${PREFIX}-modal`,
  header: `${PREFIX}-header`,
  divider: `${PREFIX}-divider`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.canvasCell}`]: {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    height: '480px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      height: '100%'
    }
  },

  [`& .${classes.modal}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  [`& .${classes.header}`]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },

  [`& .${classes.divider}`]: {
    marginLeft: '8px',
    marginRight: '8px'
  }
}));

// eslint-disable-next-line no-shadow
enum Titles {
  S = 'source',
  I = 'input',
  P = 'process',
  O = 'output',
  C = 'consumer'
}

interface SipocProps {
  elements: CanvasCellState[];
  gridHeader: string;
  inited: boolean;
}

export default function SipocGrid({ elements, gridHeader, inited }: SipocProps) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [chosenCell, setChosenCell] = React.useState('');

  const colorIndicatorClickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    setModalOpen(true);
    setChosenCell(event.currentTarget.parentElement?.id || '');
  };

  const modalCloseHandler = (): void => {
    setModalOpen(false);
    setChosenCell('');
  };

  const capitalize = ([first, ...rest]: string): string => [first.toUpperCase(), ...rest].join('');

  const getCanvasCell = (
    name: string,
    icon: JSX.Element,
    cellClass = classes.canvasCell
  ): JSX.Element | null => {
    if (!inited)
      return (
        <Skeleton
          variant="rectangular"
          className={cellClass}
          data-testid="sipoc-lower-canvas-skeleton"
        />
      );

    const element = elements.filter(item => item.name.toLowerCase() === name).pop();
    return element ? (
      <CanvasCell id={element.id} title={element.name} className={cellClass} icon={icon}>
        <Root sx={{ typography: 'body2' }}>
          {element.rows.map(row => (
            <React.Fragment key={row.id}>
              <CellRow>
                <ColorIndicator color={row.rangecolor} onClick={colorIndicatorClickHandler} />
                {row.name}
              </CellRow>

              <Divider variant="middle" classes={{ middle: classes.divider }} />
            </React.Fragment>
          ))}
        </Root>
      </CanvasCell>
    ) : (
      <CanvasCell id="" icon={icon} title={capitalize(name)} className={cellClass}>
        <Root />
      </CanvasCell>
    );
  };

  return (
    <Root>
      <Container
        maxWidth="xl"
        component="main"
        data-testid={inited ? 'sipoc-lower-main' : 'sipoc-lower-main-loading'}
      >
        <Typography
          className={`${classes.header} customHeader `}
          variant="h5"
          data-testid="sipoc-lower-header"
        >
          {inited ? (
            gridHeader
          ) : (
            <Skeleton variant="text" data-testid="sipoc-lower-header-skeleton" />
          )}
        </Typography>
        <Grid container spacing={1} data-testid="sipoc-lower-grid">
          <Grid item xs={12} sm={12} md data-testid="sipoc-lower-column">
            {getCanvasCell(Titles.S, <AirportShuttle />)}
          </Grid>
          <Grid item xs={12} sm={12} md data-testid="sipoc-lower-column">
            {getCanvasCell(Titles.I, <CustomInput />)}
          </Grid>
          <Grid item xs={12} sm={12} md data-testid="sipoc-lower-column">
            {getCanvasCell(Titles.P, <Settings />)}
          </Grid>
          <Grid item xs={12} sm={12} md data-testid="sipoc-lower-column">
            {getCanvasCell(Titles.O, <CustomOutput />)}
          </Grid>
          <Grid item xs={12} sm={12} md data-testid="sipoc-lower-column">
            {getCanvasCell(Titles.C, <LocalGroceryStore />)}
          </Grid>
        </Grid>
      </Container>
      <Modal
        data-testid="sipoc-lower-modal-window"
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        className={classes.modal}
        open={modalOpen}
        onClose={modalCloseHandler}
      >
        <div data-testid="sipoc-lower-modal-placeholder">
          {`modal placeholder for
          ${chosenCell}`}
        </div>
      </Modal>
    </Root>
  );
}
